/**
 * RouteOptimizationDemo — visuelt før/efter-eksempel der gør konsolideringen håndgribelig.
 *
 * Venstre: 5 separate kørsler (afsender → modtager → tilbage til afsender → næste).
 * Højre: 1 rute med 5 stop optimeret af vores ruteplanner.
 *
 * Når sektionen kommer i viewport (IntersectionObserver), animeres begge paneler
 * sekventielt så besøgende ser bevægelsen og besparelsen visuelt.
 *
 * SVG'erne er simplificerede repræsentationer — ikke et live Leaflet-kort. Tallene
 * er baseret på en typisk webshop-case (5 pakker i Storkøbenhavn).
 */
import React from "react";
import { TrendingDown, Clock, Route as RouteIcon, Leaf, Play } from "lucide-react";

// Simple 2D dot positions — 1 sender (square) and 5 receivers, scattered.
const SENDER = { x: 60, y: 110 };
const RECEIVERS = [
  { x: 175, y: 50, label: "Frederiksberg" },
  { x: 270, y: 95, label: "Østerbro" },
  { x: 295, y: 170, label: "Amager" },
  { x: 200, y: 200, label: "Valby" },
  { x: 130, y: 175, label: "Vesterbro" },
];

// Distance between two points (used to size stroke-dasharray for animation)
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

function Dot({ x, y, color = "#DC2626", label, kind = "circle" }) {
  return (
    <g>
      {kind === "square" ? (
        <rect x={x - 7} y={y - 7} width={14} height={14} fill={color} />
      ) : (
        <circle cx={x} cy={y} r={6} fill={color} stroke="#0A1128" strokeWidth={1.5} />
      )}
      {label && (
        <text
          x={x + 12}
          y={y + 4}
          fontSize="10"
          fontFamily="Manrope, sans-serif"
          fontWeight="600"
          fill="#0A1128"
        >
          {label}
        </text>
      )}
    </g>
  );
}

function BeforeMap({ play }) {
  // Animate each round-trip sequentially. Each pair (out + back) takes ~900ms,
  // staggered with 700ms delay between trips.
  const TRIP_MS = 900;
  const STAGGER_MS = 700;
  return (
    <svg viewBox="0 0 360 240" className="w-full h-auto">
      <rect width="360" height="240" fill="#F8FAFC" />
      {RECEIVERS.map((r, i) => {
        const d = dist(SENDER, r);
        const start = i * STAGGER_MS;
        return (
          <g key={i} opacity={play ? 0.65 : 0}>
            <path
              d={`M${SENDER.x} ${SENDER.y} L${r.x} ${r.y}`}
              stroke="#DC2626"
              strokeWidth={1.8}
              strokeDasharray={`${d}`}
              strokeDashoffset={play ? 0 : d}
              style={{
                transition: `stroke-dashoffset ${TRIP_MS / 2}ms ease-out`,
                transitionDelay: `${start}ms`,
              }}
              fill="none"
            />
            <path
              d={`M${r.x} ${r.y} L${SENDER.x} ${SENDER.y}`}
              stroke="#94A3B8"
              strokeWidth={1.2}
              strokeDasharray={`${d}`}
              strokeDashoffset={play ? 0 : d}
              style={{
                transition: `stroke-dashoffset ${TRIP_MS / 2}ms ease-in`,
                transitionDelay: `${start + TRIP_MS / 2}ms`,
              }}
              fill="none"
            />
            <style>{`
              g[data-trip="${i}"] { transition: opacity 200ms ease-out; transition-delay: ${start}ms; }
            `}</style>
          </g>
        );
      })}
      <Dot x={SENDER.x} y={SENDER.y} color="#0A1128" kind="square" label="Afsender" />
      {RECEIVERS.map((r, i) => (
        <Dot key={i} x={r.x} y={r.y} color="#DC2626" label={r.label} />
      ))}
    </svg>
  );
}

function AfterMap({ play }) {
  // Optimized order: nearest-neighbor zig-zag
  const order = [SENDER, RECEIVERS[4], RECEIVERS[0], RECEIVERS[1], RECEIVERS[2], RECEIVERS[3]];
  const pathD = order.map((p, i) => `${i === 0 ? "M" : "L"}${p.x} ${p.y}`).join(" ");
  // total polyline length for stroke animation
  const totalLen = order.reduce((sum, p, i) => (i === 0 ? 0 : sum + dist(order[i - 1], p)), 0);
  // After-map starts animating AFTER before-map finishes (5 trips × 700ms ≈ 3500ms)
  const POLY_DELAY_MS = 3600;
  const POLY_DURATION_MS = 2400;
  return (
    <svg viewBox="0 0 360 240" className="w-full h-auto">
      <rect width="360" height="240" fill="#F8FAFC" />
      <path
        d={pathD}
        stroke="#0A1128"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        strokeDasharray={`${totalLen}`}
        strokeDashoffset={play ? 0 : totalLen}
        style={{
          transition: `stroke-dashoffset ${POLY_DURATION_MS}ms cubic-bezier(.22,.61,.36,1)`,
          transitionDelay: `${POLY_DELAY_MS}ms`,
        }}
      />
      <Dot x={SENDER.x} y={SENDER.y} color="#0A1128" kind="square" label="Afsender" />
      {RECEIVERS.map((r, i) => (
        <Dot key={i} x={r.x} y={r.y} color="#DC2626" label={r.label} />
      ))}
      {/* Numbered stop badges — fade-in as the polyline reaches each stop */}
      {[1, 2, 3, 4, 5].map((n) => {
        const stopIdx = n; // 1..5 → order index 1..5
        const r = order[stopIdx];
        // Estimate when polyline reaches this stop: fraction of length traveled
        const partial = order.slice(0, stopIdx + 1).reduce(
          (s, p, i) => (i === 0 ? 0 : s + dist(order[i - 1], p)),
          0
        );
        const reachAt = POLY_DELAY_MS + (partial / totalLen) * POLY_DURATION_MS;
        return (
          <g
            key={n}
            opacity={play ? 1 : 0}
            style={{
              transition: "opacity 220ms ease-out, transform 220ms ease-out",
              transitionDelay: `${reachAt}ms`,
              transformOrigin: `${r.x}px ${r.y - 14}px`,
              transform: play ? "scale(1)" : "scale(0.6)",
            }}
          >
            <circle cx={r.x} cy={r.y - 14} r={8} fill="#0A1128" />
            <text
              x={r.x}
              y={r.y - 11}
              fontSize="10"
              fontWeight="700"
              fill="#fff"
              textAnchor="middle"
              fontFamily="Manrope, sans-serif"
            >
              {n}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-3 px-4 py-3 border border-slate-200 bg-white">
    <div
      className="w-9 h-9 flex items-center justify-center shrink-0"
      style={{ background: accent || "#0A1128", color: "#fff" }}
    >
      <Icon size={16} />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2">{label}</div>
      <div className="font-display font-bold text-base text-brand-dark truncate">{value}</div>
    </div>
  </div>
);

export default function RouteOptimizationDemo() {
  const sectionRef = React.useRef(null);
  const [play, setPlay] = React.useState(false);
  const [playKey, setPlayKey] = React.useState(0); // bump to force re-mount on replay

  React.useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    // Respect reduced-motion preference
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) {
      setPlay(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !play) {
            setPlay(true);
            // One-shot — disconnect after first play to avoid re-trigger noise
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [play]);

  const replay = () => {
    setPlay(false);
    setPlayKey((k) => k + 1);
    // small tick to let the DOM reset stroke-dashoffsets before re-playing
    setTimeout(() => setPlay(true), 60);
  };

  return (
    <section
      ref={sectionRef}
      className="bg-[#0A1128] text-white border-t border-b border-white/5"
      data-testid="b2b-route-demo"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-3xl">
            <div className="kicker text-brand-red">Rute-optimering · konkret eksempel</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-4">
              5 pakker. Én chauffør. Halvdelen af kilometerne.
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed text-lg">
              Typisk webshop-case fra Storkøbenhavn — samme 5 leveringer, men med vores
              rute-optimering kører chaufføren én sløjfe i stedet for fem turetur fra eget lager.
            </p>
          </div>
          <button
            type="button"
            onClick={replay}
            data-testid="b2b-route-replay"
            className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white text-sm px-4 py-2 mt-2 transition-colors"
          >
            <Play size={14} /> Genafspil animation
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12" key={playKey}>
          {/* BEFORE */}
          <div
            className="bg-white text-brand-dark p-6 lg:p-8 border-l-4 border-slate-300"
            data-testid="b2b-route-before"
          >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Uden optimering</div>
                <div className="font-display font-black text-xl mt-1">5 separate kørsler</div>
              </div>
              <div className="text-xs text-slate-500">Tur-retur for hver pakke</div>
            </div>
            <div className="mt-5 rounded-sm overflow-hidden">
              <BeforeMap play={play} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-5">
              <Stat icon={RouteIcon} label="Afstand" value="87 km" accent="#94A3B8" />
              <Stat icon={Clock} label="Køretid" value="4t 20m" accent="#94A3B8" />
              <Stat icon={Leaf} label="CO₂" value="14,2 kg" accent="#94A3B8" />
            </div>
          </div>

          {/* AFTER */}
          <div
            className="bg-white text-brand-dark p-6 lg:p-8 border-l-4 border-brand-red"
            data-testid="b2b-route-after"
          >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-red">Med EMA-rute</div>
                <div className="font-display font-black text-xl mt-1">1 rute · 5 stop</div>
              </div>
              <div className="text-xs text-slate-500">Nummereret leveringsrækkefølge</div>
            </div>
            <div className="mt-5 rounded-sm overflow-hidden">
              <AfterMap play={play} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-5">
              <Stat icon={RouteIcon} label="Afstand" value="38 km" accent="#DC2626" />
              <Stat icon={Clock} label="Køretid" value="1t 45m" accent="#DC2626" />
              <Stat icon={Leaf} label="CO₂" value="6,2 kg" accent="#DC2626" />
            </div>
          </div>
        </div>

        {/* Savings strip */}
        <div
          className="mt-10 bg-brand-red text-white p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6"
          data-testid="b2b-route-savings"
        >
          <div className="flex items-center gap-4">
            <TrendingDown size={36} className="shrink-0" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold opacity-80">Besparelse pr. dag</div>
              <div className="font-display font-black text-2xl lg:text-3xl mt-1">
                −56 % km · −60 % tid · −56 % CO₂
              </div>
            </div>
          </div>
          <div className="text-sm lg:text-right max-w-md">
            På månedsbasis (20 arbejdsdage) sparer en typisk webshop <strong>~980 km</strong> og{" "}
            <strong>~50 timers chaufførtid</strong> — uden at gå på kompromis med leveringstiden til kunderne.
          </div>
        </div>

        <p className="text-center text-xs text-white/50 mt-6">
          Eksemplet er en typisk Storkøbenhavn-case. Faktiske tal varierer med adresser, trafik og volume — vi udregner et konkret estimat på jeres data inden vi aftaler endelig pris.
        </p>
      </div>
    </section>
  );
}
